/* eslint-disable no-nested-ternary */
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  blueGrey, common,
} from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import CheckIcon from '@mui/icons-material/CheckCircle';
import React, {
  useEffect, useMemo, useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { unwrapResult } from '@reduxjs/toolkit';
import { camelCase, pascalCase } from 'change-case';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  enrollCompanyToPartner,
  getCompanyPartnerRelations,
} from '../../../../store/thunks/dashboardThunk';
import { selectMemberInformation } from '../../../../store/slices/onboardingSlice';
import {
  BadgeType, PartnerEnrollmentStatus, PartnerEnrollType,
} from '../../../../common/enums';
import { useCustomEventTracking } from '../../../../common/useCustomEventTracking';
import {
  canMemberContinuePartnerEnrollment,
  getPartnerEnrollmentStatus,
  hasMemberEnrolledWithDistributor,
  homeDepotPartnerId,
} from '../../../../common/utils/PartnerEnrollmentUtils';
import { ManageEnrollmentCard } from './ManageEnrollmentCard';
import { UnlinkPartnerCard } from './UnlinkPartnerCard';
import { EnrollmentCardHeader } from './PartnerEnrollmentCardCommon';
import { EnrollmentConfirmationCard } from './EnrollmentConfirmationCard';
import { getCanonicalPartnerName, PartnerName } from '../../../../common/constants';
import { PartnerSignUpFormDialog } from './PartnerSignUpFormDialog';
import { Logger } from '../../../../config/logging/logger';
import { TestIds } from '../../../../assets/testIds';
import { usePartnerSpecificFormConfigMap } from './PartnerSpecificFormConfig';
import { ConfirmAfterExternalEnrollmentCard } from './ConfirmAfterExternalEnrollmentCard';
import { usePartnerEnrollmentWithExtraStepContext } from './usePartnerEnrollmentWithExtraStep';
import { ManufacturerDistributor, usePartnerConfigList } from './usePartnerConfigList';
import OptimusButton from '../../../../common/library/Button/Button';
import { Badge } from '../../../../common/Badge';
import { useGetCompanyPartnerRelation } from './useGetCompanyPartnerRelation';
import { getMemberInformation } from '../../../../store/thunks/onboardingThunk';
import { selectGetCompanyPartnerRelations } from '../../../../store/slices/dashboardSlice';
import { HOME_DEPOT_SETUP_OR_SKIP_ROUTE, PARTNERS_ROUTE_HOME_DEPOT } from '../../../../routes/namedRoutes';
import { HD_ENROLL_MODAL_PARAM } from '../../../../common/homeDepotEnrollmentIntent';
import { useSolutionOrg } from '../../../../config/useSolutionOrg';

const cardStyle = (theme: Theme) => ({
  background: common.white,
  marginTop: theme.spacing(3),
  borderRadius: theme.spacing(1.6),
  border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
} as const);

interface PartnerEnrollmentDialogContentProps {
  optionalCardStyle: (theme: Theme) => {
    [key: string]: unknown;
  },
  inputStyles?: {
    [k: string]: unknown;
  };
  updateEnrollmentDialogOpenState: (event: boolean) => void;
  updateConfirmationDialogOpenState?: (event: boolean) => void;
  confirmationDialogOpenState?: boolean;
  partnerId: string;
  partnerLogoUrl: string;
  partnerName: PartnerName;
  partnerCode: string;
  partnerLogoImageWidth: number;
  supportsExistingAccountFlow: boolean
  supportsManufacturerFlow: boolean,
  manufacturerDistributors: ManufacturerDistributor[] | null
  hasExtraExistingAccQuestion?: boolean,
}

interface IFormInputs {
  partnerAccountNumber: string
  helpFindAccount: boolean
  // if type of this question is different from checkbox for other partner, need to update this too
  extraQuestion?: boolean
}

export default function PartnerEnrollmentDialogContent({
  optionalCardStyle,
  inputStyles,
  updateEnrollmentDialogOpenState,
  updateConfirmationDialogOpenState,
  confirmationDialogOpenState,
  partnerId,
  partnerLogoUrl,
  partnerName,
  partnerCode,
  partnerLogoImageWidth,
  supportsExistingAccountFlow,
  supportsManufacturerFlow,
  manufacturerDistributors,
  hasExtraExistingAccQuestion,
}: PartnerEnrollmentDialogContentProps) {
  const { t } = useTranslation();
  const isDesktopScreen = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { company } = useAppSelector(selectMemberInformation);
  const {
    getPartnerEnrollmentDoneWithExtraStep,
    setEnrollmentExtraStepDoneState,
    setEnrollmentFormData,
  } =
    usePartnerEnrollmentWithExtraStepContext();
  const getCompanyPartnerRelation = useGetCompanyPartnerRelation();
  const companyPartnerRelation = getCompanyPartnerRelation(partnerId);
  const companyPartnerRelations = useAppSelector(selectGetCompanyPartnerRelations);
  const partnerEnrollmentStatus = getPartnerEnrollmentStatus(companyPartnerRelation);
  const { trackPartnerEnrollment, trackRequest } = useCustomEventTracking();
  const theme = useTheme();
  const navigate = useNavigate();
  const { isNativeAppExperience } = useSolutionOrg();
  const [showExistingAccountForm, setShowExistingAccountForm] = useState(false);
  const [openSuccessSnackbar, setOpenSuccessSnackbar] = useState(false);
  const [openErrorSnackbar, setOpenErrorSnackbar] = useState(false);
  const [showUnlink, setShowUnlink] = useState(false);
  const partnerConfigList = usePartnerConfigList();
  const partnerSpecificFormConfigMap = usePartnerSpecificFormConfigMap();
  const [selectedDistributorId, setSelectedDistributorId] = useState<string | undefined>(undefined);
  const [enrolledDistributorSelected, setEnrolledDistributorSelected] = useState(false);
  const [isSubmittingEnrolledDistributorSignup,
    setIsSubmittingEnrolledDistributorSignup] = useState(false);
  const selectedDistributorConfig = useMemo(() => partnerConfigList?.find(
    (pc) => pc.id && pc.id === selectedDistributorId,
  ), [partnerConfigList, selectedDistributorId]);
  const [loading, setLoading] = useState(false);
  // open sign up form on initial render if existing account flow is not supported
  // and doesn't require distributor/manufacturer selection
  const [openSignUpForm, setOpenSignUpForm] = useState(
    !supportsExistingAccountFlow && !supportsManufacturerFlow,
  );
  const UNKNOWN_DISTRIBUTOR = 'Unknown Distributor';

  const [PartnerCardComponent, setPartnerCardComponent] =
    useState<React.FC | null>(null);
  const {
    handleSubmit,
    control,
    formState,
    reset,
    setValue,
    getValues,
    watch,
  } = useForm<IFormInputs>({ mode: 'onChange' });
  const logger = Logger.create('PartnerEnrollmentDialogContent');

  // Derive i18n keys, test IDs and analytics event names from the stable partner
  // CODE (via its canonical name) rather than the live Retool name, so a rename
  // never breaks string/testId lookups or changes historical event names.
  // Falls back to the live name for any partner without a canonical mapping.
  const partnerKeyName = getCanonicalPartnerName(partnerCode, partnerName);

  const onPartnerAccountNumberSubmit = handleSubmit((data: IFormInputs) => {
    // Key the form config off the stable partner code (SN-838), not the name.
    const config = partnerSpecificFormConfigMap[partnerCode];
    const companyId = company?.id;
    const accountNumber = data.partnerAccountNumber.trim();
    if (!companyId || (!accountNumber && !data.helpFindAccount)) {
      logger.warn(Logger.format('Failed to submit: account number form not completed'));
      return;
    }

    let partnerSpecificQuestions: Record<string, unknown> | undefined;
    if (hasExtraExistingAccQuestion) {
      partnerSpecificQuestions = config?.existingAccountExtraQuestion?.reduce((acc, q) => {
        acc.questions[q.key] = {
          title: q.title,
          answer: data.extraQuestion ? 'Yes' : 'No',
        };
        return acc;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }, { version: config?.version, questions: {} } as any);
    }

    dispatch(enrollCompanyToPartner({
      companyId,
      partnerId,
      data: {
        type: PartnerEnrollType.ExistingAccountNumber,
        enrollData: accountNumber ? {
          accountNumber,
          ...(partnerSpecificQuestions && { partnerSpecificQuestions }),
        } : {
          accountNumberForgotten: true,
          ...(partnerSpecificQuestions && { partnerSpecificQuestions }),
        },
      },
    }))
      .then(unwrapResult)
      .then(() => {
        reset({ partnerAccountNumber: '' });
        setOpenSuccessSnackbar(true);
        setOpenErrorSnackbar(false);
        updateConfirmationDialogOpenState?.(true);

        trackPartnerEnrollment('Submit', `${pascalCase(partnerKeyName)}AccountNumberForm`, {
          [`${camelCase(partnerKeyName)}AccountNumber`]: data.partnerAccountNumber,
          isOptimizelyEvent: true,
        });

        // fire event to indicate company submitted partner enrollment
        trackPartnerEnrollment('Submit', 'Form', {
          partnerName,
          enrollmentType: 'Existing',
          isOptimizelyEvent: true,
        });
      })
      .catch((error: unknown) => {
        setOpenSuccessSnackbar(false);
        setOpenErrorSnackbar(true);
      });
  });

  const signUpManufacturerWithEnrolledDistributor = async () => {
    setIsSubmittingEnrolledDistributorSignup(true);
    try {
      const companyId = company?.id;
      const distributorId = selectedDistributorConfig?.id;
      if (!companyId || !distributorId) {
        throw new Error('Company or distributor not found');
      }

      await dispatch(enrollCompanyToPartner({
        companyId,
        partnerId,
        data: {
          type: PartnerEnrollType.Manufacturer,
          enrollData: { distributorId },
        },
      })).then(unwrapResult);

      setOpenSuccessSnackbar(true);
      setOpenErrorSnackbar(false);
      updateConfirmationDialogOpenState?.(true);

      dispatch(getMemberInformation({ trackRequest }));
      dispatch(getCompanyPartnerRelations(companyId));

      // fire event to indicate member has signed up with manufacturer (used for webhook)
      trackPartnerEnrollment('Submit', `${pascalCase(partnerKeyName)}SignupForm`, {
        formData: null,
        isOptimizelyEvent: true,
      });
      // fire event to indicate company submitted manufacturer enrollment
      trackPartnerEnrollment('Submit', 'Form', {
        partnerName,
        enrollmentType: 'New',
        isOptimizelyEvent: true,
      });
      setIsSubmittingEnrolledDistributorSignup(false);
    } catch (error: unknown) {
      setOpenSuccessSnackbar(false);
      setOpenErrorSnackbar(true);
      setIsSubmittingEnrolledDistributorSignup(false);
    }
  };

  // Home Depot isn't enrolled yet, so link this manufacturer to Home Depot now (the
  // backend snapshots Behr/HDX's enrollmentStatus from Home Depot's current status,
  // same mechanism used once HD is already Completed) and send the member to finish
  // HD enrollment before returning.
  const enrollWithHomeDepotAndRedirect = async () => {
    setIsSubmittingEnrolledDistributorSignup(true);
    try {
      const companyId = company?.id;
      if (!companyId) {
        throw new Error('Company not found');
      }

      await dispatch(enrollCompanyToPartner({
        companyId,
        partnerId,
        data: {
          type: PartnerEnrollType.Manufacturer,
          enrollData: { distributorId: homeDepotPartnerId },
        },
      })).then(unwrapResult);

      dispatch(getCompanyPartnerRelations(companyId));

      const returnPath = location.pathname;
      const homeDepotRedirectPath = isNativeAppExperience ?
        `${HOME_DEPOT_SETUP_OR_SKIP_ROUTE}?returnPath=${returnPath}` +
        '&userInitiatedHdEnrollment=true' :
        `${PARTNERS_ROUTE_HOME_DEPOT}?${HD_ENROLL_MODAL_PARAM}=true`;

      trackPartnerEnrollment('Open', 'HomeDepotSignupForm');
      navigate(homeDepotRedirectPath, {
        state: {
          userInitiatedHdEnrollment: true,
          returnPath,
          // Pass initiating partner info for auto-enrollment after HD success
          initiatingPartnerName: partnerName,
          initiatingPartnerCode: partnerCode,
          initiatingPartnerId: partnerId,
        },
      });
      setOpenErrorSnackbar(false);
      setIsSubmittingEnrolledDistributorSignup(false);
      // Close the current enrollment dialog
      updateEnrollmentDialogOpenState(false);
    } catch (error: unknown) {
      setOpenErrorSnackbar(true);
      setIsSubmittingEnrolledDistributorSignup(false);
    }
  };

  const completeExternalEnrollmentStep = async () => {
    const companyId = company?.id;
    if (!companyId || loading) return;

    setLoading(true);
    try {
      await dispatch(enrollCompanyToPartner({
        companyId,
        partnerId,
        data: {
          type: PartnerEnrollType.DirectEnroll,
          enrollData: null,
        },
      })).then(unwrapResult);

      trackPartnerEnrollment(
        'Complete',
        `${pascalCase(partnerKeyName)}RequiredAction`,
      );
      setOpenErrorSnackbar(false);
      setEnrollmentExtraStepDoneState(partnerCode, false);
      setEnrollmentFormData(partnerCode, null);
      updateEnrollmentDialogOpenState(false);
    } catch {
      setOpenSuccessSnackbar(false);
      setOpenErrorSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const PartnerSignupOrExistingAccountCard = () => (
    <Box sx={{
      mt: theme.spacing(-2),
    }}>
      <Divider sx={{
        mb: theme.spacing(3),
      }} />
      <Grid container
        sx={{
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Grid item xs={12}
          sx={{
            justifyContent: 'center !important',
            [theme.breakpoints.down('md')]: {
              justifyContent: 'center',
            },
            '& .MuiStack-root': {
              justifyContent: 'center',
            },
          }}
        >
          <OptimusButton
            data-testid={(TestIds.rewardsPage[camelCase(partnerKeyName) as
              keyof typeof TestIds.rewardsPage] as {
              signUpOrExistingAccForm: {
                proceedEnrollmentBtn: string
                existingAccountBtn: string
              }
            })?.signUpOrExistingAccForm?.proceedEnrollmentBtn}
            buttonText={`${t('button.ProceedToEnrollment')}`}
            type={'submit'}
            customVariant='primary'
            size='l'
            onClick={() => {
              trackPartnerEnrollment('Open', `${pascalCase(partnerKeyName)}SignupForm`);
              setOpenSignUpForm(true);
            }}
          />
        </Grid>
        <Grid item xs={12}
          sx={{
            justifyContent: 'center !important',
            [theme.breakpoints.down('md')]: {
              justifyContent: 'center',
            },
            '& .MuiStack-root': {
              justifyContent: 'center',
            },
          }}
        >
          <OptimusButton
            data-testid={(TestIds.rewardsPage[camelCase(partnerKeyName) as
              keyof typeof TestIds.rewardsPage] as {
              signUpOrExistingAccForm: {
                proceedEnrollmentBtn: string
                existingAccountBtn: string
              }
            })?.signUpOrExistingAccForm?.existingAccountBtn}
            buttonText={t(`${camelCase(partnerKeyName)}.existingAccountLink`) as string}
            type={'submit'}
            customVariant='secondaryGray'
            size='l'
            onClick={() => {
              trackPartnerEnrollment('Open', `${pascalCase(partnerKeyName)}AccountNumberForm`);
              setShowExistingAccountForm(true);
            }}
            sx={{
              marginTop: theme.spacing(1),
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const handleSelectDistributor = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDistributorId(event.target.value);
    setEnrolledDistributorSelected(hasMemberEnrolledWithDistributor(getPartnerEnrollmentStatus(
      getCompanyPartnerRelation(event.target.value),
    )));
  };

  const DistributorsOfManufacturerCard = () => (
    <Box sx={{
      mt: theme.spacing(-2),
    }}>
      <Divider sx={{
        mb: theme.spacing(4),
      }} />
      <Box sx={{ mb: theme.spacing(2.5) }}>
        <Typography variant='body8'>
          {t(`${camelCase(partnerKeyName)}.signUpDialog.heading2`)}
        </Typography>
      </Box>
      <RadioGroup
        value={selectedDistributorId}
        onChange={handleSelectDistributor}
      >
        <Grid container spacing={2}>
          {manufacturerDistributors
            ?.filter((d) => partnerConfigList.some((p) => p.id === d.partnerId))
            .map((d) => {
              const isSelected = !!(selectedDistributorId && selectedDistributorId === d.partnerId);
              const distributorEnrollmentStatus = getPartnerEnrollmentStatus(
                getCompanyPartnerRelation(d.partnerId),
              );
              // Prefer the live partner name (resolved by id) over the hard-coded
              // fallback, so a Retool rename shows the current display name.
              const distributorDisplayName = partnerConfigList.find(
                (p) => p.id === d.partnerId,
              )?.partnerName ?? d.partnerName;
              return (<Grid item xs={12} key={d.partnerId}>
                <Grid container
                  sx={{
                    border: `1px solid ${isSelected ?
                      theme.palette.brand.primary :
                      theme.palette.grey[300]}`,
                    borderRadius: theme.spacing(1.25),
                    padding: theme.spacing(1),
                    alignItems: 'center',
                  }}
                >
                  <Grid item xs={9} sm={10}>
                    <FormControlLabel
                      value={d.partnerId}
                      control={
                        <Radio
                          sx={{
                            '&.Mui-checked': {
                              color: theme.palette.brand.primary,
                            },
                            '&:hover': {
                              backgroundColor: theme.palette.brand.primary,
                            },
                          }}
                        />}
                      label={distributorDisplayName}
                      sx={{ margin: 0, width: '100%' }}
                    />
                  </Grid>
                  {hasMemberEnrolledWithDistributor(distributorEnrollmentStatus) &&
                    <Grid item xs={3} sm={2}>
                      <Badge
                        badgeType={BadgeType.Checkmark}
                        badgeText={
                          distributorEnrollmentStatus === PartnerEnrollmentStatus.Completed ?
                            'Enrolled' :
                            t('partnerDetails.enrollmentStatus.inProgress')
                        }
                      />
                    </Grid>}
                </Grid>
              </Grid>);
            })}
        </Grid>
      </RadioGroup>
      <Grid container
        sx={{
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <Grid item xs={12}
          sx={{
            justifyContent: 'center !important',
            [theme.breakpoints.down('md')]: {
              justifyContent: 'center',
            },
            '& .MuiStack-root': {
              justifyContent: 'center',
            },
          }}
        >
          <OptimusButton
            size={'l'}
            sx={{
              mb: theme.spacing(1),
              mt: theme.spacing(4),
            }}
            disabled={!selectedDistributorId || isSubmittingEnrolledDistributorSignup}
            buttonText={isSubmittingEnrolledDistributorSignup ?
              <CircularProgress color="inherit" size={24} /> :
              (
                (enrolledDistributorSelected ?
                  t(`${camelCase(partnerKeyName)}.signUpDialog.completeEnrollmentBtn`) :
                  (selectedDistributorId ?
                    `${t(`${camelCase(partnerKeyName)}.signUpDialog.proceedToEnrollWithBtn`)} ${selectedDistributorConfig?.title
                    }` :
                    t(`${camelCase(partnerKeyName)}.signUpDialog.proceedToEnrollBtn`))) as string
              )
            }
            onClick={() => {
              if (enrolledDistributorSelected) {
                signUpManufacturerWithEnrolledDistributor();
              } else if (selectedDistributorId === homeDepotPartnerId) {
                enrollWithHomeDepotAndRedirect();
              } else {
                trackPartnerEnrollment('Open', `${pascalCase(getCanonicalPartnerName(
                  selectedDistributorConfig?.partnerCode,
                  selectedDistributorConfig?.partnerName || UNKNOWN_DISTRIBUTOR,
                ))}SignupForm`);
                setOpenSignUpForm(true);
              }
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );

  const PartnerAccountNumberForm = () => {
    const watchedPartnerAccountNumber = watch('partnerAccountNumber');
    const watchedHelpFindAccount = watch('helpFindAccount');
    const watchedExtraQuestion = watch('extraQuestion');
    return (
      <>
        <Divider sx={{
          mb: theme.spacing(3),
        }} />
        <Box>
          <form onSubmit={onPartnerAccountNumberSubmit}>
            <Typography sx={{ color: theme.palette.grey[500] }}>
              {t(`${camelCase(partnerKeyName)}.enterAccountNumber`)}
            </Typography>
            <Grid container
              sx={{
                alignItems: 'center',
                textAlign: 'center',
                pt: theme.spacing(1),
                width: '100%',
              }}
            >
              <Grid item xs={12}>
                <Controller
                  name="partnerAccountNumber"
                  control={control}
                  defaultValue={''}
                  render={({
                    field: {
                      onChange, onBlur, value,
                    },
                  }) => <TextField
                    onChange={(event) => {
                      // clear checkbox if account number is typed
                      if (event.target.value && getValues('helpFindAccount')) {
                        setValue('helpFindAccount', false);
                      }
                      onChange(event);
                    }}
                    onBlur={onBlur}
                    value={value}
                    type="text"
                    placeholder={t(`${camelCase(partnerKeyName)}.accountNumberPlaceholder`) as string}
                    InputProps={{
                      sx: {
                        borderRadius: theme.spacing(1),
                        border: `1px solid ${blueGrey[200]}`,
                        background: common.white,
                        fontWeight: '400',
                        fontSize: theme.spacing(2),
                        paddingLeft: theme.spacing(2),
                      },
                    }}
                    sx={{
                      width: '100%',
                      ...(inputStyles && inputStyles),
                    }}
                  />}
                />
                {(formState?.errors?.partnerAccountNumber &&
                  formState.errors.partnerAccountNumber.type !== 'required') &&
                  <Alert
                    sx={{
                      background: common.white,
                      color: theme.palette.error.main,
                      p: 0,
                    }}
                    severity='error'>
                    {formState?.errors.partnerAccountNumber.message}
                  </Alert>}
              </Grid>
              <Grid item xs={12} sx={{
                textAlign: 'start',
                mt: theme.spacing(2.5),
              }}>
                <Box display={'flex'}>
                  <Controller
                    name={'helpFindAccount'}
                    control={control}
                    defaultValue={false}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox
                        disableRipple
                        data-testid={(TestIds.rewardsPage[camelCase(partnerKeyName) as
                          keyof typeof TestIds.rewardsPage] as {
                          existingAccForm: {
                            submitBtn: string,
                            goBackBtn: string,
                            noAccountNumberCheckbox: string
                          },
                        })?.existingAccForm?.noAccountNumberCheckbox}
                        sx={{
                          paddingTop: theme.spacing(0),
                          paddingLeft: theme.spacing(0),
                          marginTop: theme.spacing(-0.5),
                          '&.Mui-checked': {
                            color: theme.palette.brand.primary,
                          },
                        }}
                        checked={value}
                        onChange={(e) => {
                          const { checked } = e.target;
                          // clear account number if checkbox is selected
                          if (checked && getValues('partnerAccountNumber')) {
                            setValue('partnerAccountNumber', '');
                          }
                          onChange(e.target.checked);
                        }}
                      />
                    )}
                  />
                  <Box display={'flex'} flexDirection={'column'}>
                    <Typography variant={'body7'} component={'div'}>{t(`${camelCase(partnerKeyName)}.helpFindAccount`)}</Typography>
                    <Typography variant={'body8'} component={'div'}
                      sx={{
                        color: theme.palette.grey[700],
                      }}
                    >
                      {t(`${camelCase(partnerKeyName)}.helpFindAccount2`)}
                    </Typography>
                  </Box>
                </Box>
                {!hasExtraExistingAccQuestion &&
                  <Divider sx={{
                    mt: theme.spacing(4),
                    mb: theme.spacing(0),
                  }} />
                }
              </Grid>
              {hasExtraExistingAccQuestion &&
                <Grid item xs={12} sx={{
                  textAlign: 'start',
                  mt: theme.spacing(2.5),
                }}>
                  <Box display={'flex'}>
                    <Controller
                      name={'extraQuestion'}
                      control={control}
                      defaultValue={false}
                      render={({ field: { onChange, value } }) => (
                        <Checkbox
                          disableRipple
                          data-testid={(TestIds.rewardsPage[camelCase(partnerKeyName) as
                            keyof typeof TestIds.rewardsPage] as {
                            existingAccForm: {
                              submitBtn: string,
                              goBackBtn: string,
                              noAccountNumberCheckbox: string
                              makeEdgeExclusiveCheckbox: string
                            },
                          })?.existingAccForm?.makeEdgeExclusiveCheckbox}
                          sx={{
                            paddingTop: theme.spacing(0),
                            paddingLeft: theme.spacing(0),
                            marginTop: theme.spacing(-0.5),
                            '&.Mui-checked': {
                              color: theme.palette.brand.primary,
                            },
                          }}
                          checked={value}
                          onChange={(e) => onChange(e.target.checked)}
                        />
                      )}
                    />
                    <Box display={'flex'} flexDirection={'column'}>
                      <Typography variant={'body7'} component={'div'}>{t('sunbeltRentals.partnerSpecificFormConfig.extra_existing_account_question_label1')}</Typography>
                      <Typography variant={'body8'} component={'div'}
                        sx={{
                          color: theme.palette.grey[700],
                        }}
                      >
                        {t('sunbeltRentals.partnerSpecificFormConfig.extra_existing_account_question_label2')}
                      </Typography>
                    </Box>
                  </Box>
                  <Divider sx={{
                    mt: theme.spacing(4),
                    mb: theme.spacing(0),
                  }} />
                </Grid>
              }
              <Grid item xs={12}>
                <OptimusButton
                  data-testid={(TestIds.rewardsPage[camelCase(partnerKeyName) as
                    keyof typeof TestIds.rewardsPage] as {
                    existingAccForm: {
                      submitBtn: string,
                      goBackBtn: string,
                    },
                  })?.existingAccForm?.submitBtn}
                  buttonText={`${t('button.submit')}`}
                  type={'submit'}
                  customVariant='primary'
                  size='l'
                  disabled={
                    (!watchedPartnerAccountNumber &&
                      !watchedHelpFindAccount) ||
                    (hasExtraExistingAccQuestion && !watchedExtraQuestion)
                  }
                  sx={{
                    marginTop: theme.spacing(3),
                    marginBottom: theme.spacing(2),
                  }}
                />
              </Grid>
            </Grid>
          </form >
          <OptimusButton
            data-testid={(TestIds.rewardsPage[camelCase(partnerKeyName) as
              keyof typeof TestIds.rewardsPage] as {
              existingAccForm: {
                submitBtn: string,
                goBackBtn: string,
              },
            })?.existingAccForm?.goBackBtn}
            buttonText={t('button.goBack') as string}
            type={'button'}
            customVariant='secondaryGray'
            size='l'
            onClick={() => {
              trackPartnerEnrollment('Close', `${pascalCase(partnerKeyName)}AccountNumberForm`);
              setShowExistingAccountForm(false);
            }}
          />
        </Box >
      </>
    );
  };

  const handleClose = () => {
    setOpenErrorSnackbar(false);
    setOpenSuccessSnackbar(false);
  };

  // Auto-select radio button when there's only one distributor (for Behr enrollment)
  useEffect(() => {
    if (supportsManufacturerFlow &&
      manufacturerDistributors &&
      manufacturerDistributors.length === 1 &&
      !selectedDistributorId) {
      const singleDistributor = manufacturerDistributors[0];
      setSelectedDistributorId(singleDistributor.partnerId);
      setEnrolledDistributorSelected(hasMemberEnrolledWithDistributor(getPartnerEnrollmentStatus(
        getCompanyPartnerRelation(singleDistributor.partnerId),
      )));
    }
  }, [
    manufacturerDistributors,
    selectedDistributorId,
    supportsManufacturerFlow,
    getCompanyPartnerRelation,
  ]);

  useEffect(() => {
    setPartnerCardComponent(() => (
      () => {
        if (partnerSpecificFormConfigMap[partnerCode]?.hasExtraEnrollmentSteps &&
          getPartnerEnrollmentDoneWithExtraStep(partnerCode)) {
          return <ConfirmAfterExternalEnrollmentCard
            partnerName={partnerName}
            partnerCode={partnerCode}
            handleClick={completeExternalEnrollmentStep}
            loading={loading}
          />;
        }

        if (confirmationDialogOpenState) {
          return (
            <EnrollmentConfirmationCard
              partnerName={partnerName}
              partnerCode={partnerCode}
              isExistingAccountConfirmation={
                !!(companyPartnerRelation?.partnerMemberKey ||
                  companyPartnerRelation?.partnerMemberKeyForgotten)
              }
              handleExternalAction={
                partnerSpecificFormConfigMap[partnerName]
                  ?.completeEnrollmentOnConfirmationClick &&
                !(companyPartnerRelation?.partnerMemberKey ||
                  companyPartnerRelation?.partnerMemberKeyForgotten) ?
                  completeExternalEnrollmentStep :
                  undefined
              }
              loading={loading}
              handleClose={() => {
                updateEnrollmentDialogOpenState(false);
                updateConfirmationDialogOpenState?.(false);
              }}
            />
          );
        }

        if (partnerEnrollmentStatus === PartnerEnrollmentStatus.Completed) {
          // Resolve the stable code from the enrolled partner's id; the relation
          // itself only carries the (renameable) partnerName.
          const enrolledPartnerCode = partnerConfigList.find(
            (p) => p.id === companyPartnerRelation?.partnerId,
          )?.partnerCode;
          return (
            <ManageEnrollmentCard
              partnerName={companyPartnerRelation?.partnerName || ''}
              partnerCode={enrolledPartnerCode}
              setShowUnlink={setShowUnlink}
            />
          );
        }

        // Keep confirmation states read-only until the API returns a new enrollment status.
        if (!canMemberContinuePartnerEnrollment(partnerEnrollmentStatus)) {
          return null;
        }

        // These partners require an external account/application after the questionnaire.
        // Reopen the external next-step CTA instead of asking the member to resubmit the form.
        if (partnerEnrollmentStatus === PartnerEnrollmentStatus.ActionRequired &&
          !supportsExistingAccountFlow &&
          !supportsManufacturerFlow) {
          return (
            <EnrollmentConfirmationCard
              partnerName={partnerName}
              partnerCode={partnerCode}
              isExistingAccountConfirmation={false}
              handleExternalAction={
                partnerSpecificFormConfigMap[partnerName]
                  ?.completeEnrollmentOnConfirmationClick ?
                  completeExternalEnrollmentStep :
                  undefined
              }
              loading={loading}
              handleClose={() => updateEnrollmentDialogOpenState(false)}
            />
          );
        }

        // Both NotStarted and ActionRequired must keep the member-facing next step available.
        if (showExistingAccountForm) {
          return <PartnerAccountNumberForm />;
        }

        if (openSignUpForm) {
          return (
            <PartnerSignUpFormDialog
              updateConfirmationDialogOpenState={updateConfirmationDialogOpenState}
              updateEnrollmentDialogOpenState={updateEnrollmentDialogOpenState}
              partnerName={
                (selectedDistributorConfig ?
                  selectedDistributorConfig.partnerName :
                  partnerName) as PartnerName
              }
              partnerCode={
                selectedDistributorConfig ?
                  selectedDistributorConfig.partnerCode :
                  partnerCode
              }
              manufacturerName={
                supportsManufacturerFlow ? partnerName : undefined
              }
              manufacturerCode={
                supportsManufacturerFlow ? partnerCode : undefined
              }
            />
          );
        }

        if (supportsManufacturerFlow) {
          return <DistributorsOfManufacturerCard />;
        }

        if (supportsExistingAccountFlow) {
          return <PartnerSignupOrExistingAccountCard />;
        }

        return null;
      }
    ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showExistingAccountForm, companyPartnerRelation, confirmationDialogOpenState,
    updateConfirmationDialogOpenState,
    partnerEnrollmentStatus, openSignUpForm, loading,
    getPartnerEnrollmentDoneWithExtraStep,
    selectedDistributorId,
    enrolledDistributorSelected,
    isSubmittingEnrolledDistributorSignup,
  ]);

  const enrollmentCardHeaderTitles = useMemo(() => {
    const basePartner = camelCase(partnerKeyName);
    const selectedDistributorKeyName = getCanonicalPartnerName(
      selectedDistributorConfig?.partnerCode,
      selectedDistributorConfig?.partnerName,
    );

    // === Title logic ===
    let title: string;
    if (
      confirmationDialogOpenState &&
      (companyPartnerRelation?.partnerMemberKey ||
        companyPartnerRelation?.partnerMemberKeyForgotten)
    ) {
      title = t(`${basePartner}.signUpDialog.headingExistingAccConfirmation`);
    } else if (showExistingAccountForm) {
      title = t(`${basePartner}.signUpDialog.headingExistingAcc`);
    } else if (selectedDistributorConfig && openSignUpForm) {
      title = t(`${camelCase(selectedDistributorKeyName)}.signUpDialog.heading1`);
    } else if (supportsManufacturerFlow &&
      partnerEnrollmentStatus !== PartnerEnrollmentStatus.NotStarted) {
      title = t(`${basePartner}.enrollmentDialog.heading1`);
    } else {
      title = t(`${basePartner}.signUpDialog.heading1`);
    }

    // === Subtitle logic ===
    let subtitle: string;
    if (showExistingAccountForm) {
      subtitle = t(`${basePartner}.signUpDialog.heading1bExistingAcc`);
    } else if (selectedDistributorConfig && openSignUpForm) {
      subtitle = t(`${camelCase(selectedDistributorKeyName)}.signUpDialog.heading1b`);
    } else if (supportsManufacturerFlow &&
      partnerEnrollmentStatus !== PartnerEnrollmentStatus.NotStarted) {
      subtitle = t(`${basePartner}.enrollmentDialog.heading1b`);
    } else {
      subtitle = t(`${basePartner}.signUpDialog.heading1b`);
    }

    return {
      title,
      subtitle,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    companyPartnerRelation?.partnerMemberKey,
    companyPartnerRelation?.partnerMemberKeyForgotten,
    partnerEnrollmentStatus,
    supportsManufacturerFlow,
    confirmationDialogOpenState,
    openSignUpForm,
    partnerName,
    selectedDistributorConfig,
    showExistingAccountForm,
  ]);

  return (
    <>
      <Snackbar
        open={openSuccessSnackbar}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="success" sx={{ width: '100%' }}>
          {t('success.dashboard')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={openErrorSnackbar}
        autoHideDuration={4000}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      <Box
        sx={isDesktopScreen ? [cardStyle, optionalCardStyle] : [cardStyle,
          {
            p: theme.spacing(0),
            border: theme.spacing(0),
          },
        ]}
      >
        {showUnlink ? <UnlinkPartnerCard
          partnerId={partnerId}
          partnerName={companyPartnerRelation?.partnerName || ''}
          partnerCode={partnerCode}
          title={t(`${camelCase(partnerKeyName)}.unlinkDialogConfirmation.title`)}
          description={(() => {
            // Get the config for current partner
            const currentPartnerConfig = partnerConfigList.find((pc) => pc.id === partnerId);
            const isManufacturer = currentPartnerConfig?.isManufacturer;

            if (!isManufacturer) {
              // Check if there are any manufacturers enrolled through this distributor
              const enrolledManufacturers = companyPartnerRelations?.filter(
                (relation) => relation.enrolledDistributorOrManufacturerId === partnerId &&
                  getPartnerEnrollmentStatus(relation) === PartnerEnrollmentStatus.Completed,
              );

              // Filter to find manufacturers that were NOT enrolled independently
              const manufacturersToBeUnlinked = enrolledManufacturers?.filter(
                (manufacturer) => !(
                  manufacturer.partnerMemberKey ||
                  manufacturer.partnerMemberKeyForgotten
                ),
              );

              if (manufacturersToBeUnlinked && manufacturersToBeUnlinked.length > 0) {
                // Show warning message that manufacturers (e.g. Rinnai, NOVO) will also be unlinked
                const manufacturers = manufacturersToBeUnlinked
                  .map((m) => partnerConfigList.find((pc) => pc.id === m.partnerId)?.partnerName)
                  .filter(Boolean)
                  .join(' and ');
                return [
                  t(`${camelCase(partnerKeyName)}.unlinkDialogConfirmation.descriptionWithManufacturersLine1`, { manufacturers }),
                  t(`${camelCase(partnerKeyName)}.unlinkDialogConfirmation.descriptionWithManufacturersLine2`, { manufacturers }),
                ];
              }
            }
            return t(`${camelCase(partnerKeyName)}.unlinkDialogConfirmation.description`);
          })()}
          setOpenUnlink={updateEnrollmentDialogOpenState}
        /> : <>
          <EnrollmentCardHeader
            title={enrollmentCardHeaderTitles.title}
            subtitle={enrollmentCardHeaderTitles.subtitle}
            logo={selectedDistributorConfig && openSignUpForm ?
              selectedDistributorConfig.partnerLogoUrl :
              partnerLogoUrl}
            imageWidth={partnerLogoImageWidth}
          >
            {
              partnerEnrollmentStatus === PartnerEnrollmentStatus.Completed ?
                <CheckIcon color="success" fontSize="small" /> : null}
          </EnrollmentCardHeader>
          {PartnerCardComponent && <PartnerCardComponent />}
        </>
        }
      </Box>
    </>
  );
}
